"use client";

import type { ComponentType } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { Check, X } from "@/components/icons";

type IconComponent = ComponentType<{
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  "aria-hidden"?: boolean | "true" | "false";
}>;

// `icon` is optional — when omitted the column's check/X marker is used, so
// existing consumers stay unchanged.
export type ProConsItem = { label: string; body: string; icon?: IconComponent };

export function ProConsColumn({
  heading,
  items,
  positive,
}: {
  heading: string;
  items: ProConsItem[];
  positive: boolean;
}) {
  const reduced = useReducedMotion() ?? false;
  const color = positive ? "#5b8cff" : "#FCA5A5";
  const Icon = positive ? Check : X;

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={scrollRevealViewport}
      transition={{ duration: 0.45 }}
      className="border-border bg-card rounded-2xl border p-6 sm:p-8"
    >
      <div className="border-border mb-5 flex items-center gap-3 border-b pb-4">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-lg"
          style={{ background: `${color}15`, border: `1px solid ${color}40` }}
        >
          <Icon size={16} aria-hidden="true" style={{ color }} />
        </span>
        <h3 className="text-foreground text-lg font-semibold">{heading}</h3>
      </div>
      <ul className="flex list-none flex-col gap-5">
        {items.map((it) => {
          const ItemIcon = it.icon ?? Icon;
          return (
            <li key={it.label} className="flex items-start gap-3">
              <ItemIcon
                size={16}
                className="mt-0.5 shrink-0"
                style={{ color }}
                aria-hidden="true"
              />
              <div className="flex flex-col gap-1">
                <span className="text-foreground text-sm font-semibold">{it.label}</span>
                <span className="text-muted-foreground text-sm leading-relaxed">{it.body}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}
